import type { IInputController } from './IInputController';

import { mitTempo, RENN_TASTEN } from './rennen';

const MOVE_KEYS = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);
/** Ab dieser Zeigerbewegung gilt ein Klick als Ziehen und laeuft nicht los. */
const DRAG_THRESHOLD_PX = 6;

/**
 * Eingabe fuer die Draufsicht: WASD/Pfeile bewegen entlang der Weltachsen,
 * E/Leertaste interagiert, C klingelt den naechsten Peer an, Linksklick auf
 * die Karte laeuft zum Ziel. Waehrend in einem Formularfeld getippt wird oder
 * ein Modal offen ist, werden Spiel-Eingaben ignoriert.
 */
export class TopDownInput implements IInputController {
  private pressed = new Set<string>();
  private interactCallback: (() => void) | null = null;
  private ringCallback: (() => void) | null = null;
  private pickCallback: ((screenX: number, screenY: number) => void) | null = null;
  private host: HTMLElement | null = null;
  private pointerDownAt: { x: number; y: number; button: number } | null = null;
  /** Wird von aussen gesetzt, wenn ein Modal die Eingabe besitzt. */
  blocked = false;

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (this.isTyping() || this.blocked) return;
    if (MOVE_KEYS.has(event.code)) {
      this.pressed.add(event.code);
      event.preventDefault();
    }
    if (RENN_TASTEN.has(event.code)) this.pressed.add(event.code);
    if ((event.code === 'KeyE' || event.code === 'Space') && !event.repeat) {
      event.preventDefault();
      this.interactCallback?.();
    }
    if (event.code === 'KeyC' && !event.repeat) {
      event.preventDefault();
      this.ringCallback?.();
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.pressed.delete(event.code);
  };

  private readonly onBlur = (): void => {
    this.pressed.clear();
  };

  private readonly onPointerDown = (event: PointerEvent): void => {
    // Nur der erste Finger laeuft los; ein zweiter (Zoomgeste) bricht ab
    if (!event.isPrimary) {
      this.pointerDownAt = null;
      return;
    }
    this.pointerDownAt = { x: event.clientX, y: event.clientY, button: event.button };
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    const down = this.pointerDownAt;
    this.pointerDownAt = null;
    if (!event.isPrimary) return;
    if (!down || down.button !== 0 || event.button !== 0) return;
    if (Math.hypot(event.clientX - down.x, event.clientY - down.y) > DRAG_THRESHOLD_PX) return;
    if (this.blocked || !this.host) return;
    const rect = this.host.getBoundingClientRect();
    this.pickCallback?.(event.clientX - rect.left, event.clientY - rect.top);
  };

  attach(host: HTMLElement): void {
    this.host = host;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    host.addEventListener('pointerdown', this.onPointerDown);
    host.addEventListener('pointerup', this.onPointerUp);
  }

  detach(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.host?.removeEventListener('pointerdown', this.onPointerDown);
    this.host?.removeEventListener('pointerup', this.onPointerUp);
    this.host = null;
  }

  onInteract(callback: () => void): void {
    this.interactCallback = callback;
  }

  onRing(callback: () => void): void {
    this.ringCallback = callback;
  }

  onPick(callback: (screenX: number, screenY: number) => void): void {
    this.pickCallback = callback;
  }

  /** Die Draufsicht kennt keinen freien Blickwinkel. */
  look(): null {
    return null;
  }

  /** In der Draufsicht gibt es nichts einzustellen. */
  applySettings(): void {
    /* absichtlich leer */
  }

  /** Bewegungsvektor: diagonal normiert, mit Umschalt aufs Renntempo gestreckt. */
  vector(): { x: number; y: number } {
    if (this.blocked) return { x: 0, y: 0 };
    let x = 0;
    let y = 0;
    if (this.pressed.has('KeyA') || this.pressed.has('ArrowLeft')) x -= 1;
    if (this.pressed.has('KeyD') || this.pressed.has('ArrowRight')) x += 1;
    if (this.pressed.has('KeyW') || this.pressed.has('ArrowUp')) y -= 1;
    if (this.pressed.has('KeyS') || this.pressed.has('ArrowDown')) y += 1;
    if (x !== 0 && y !== 0) {
      const inv = 1 / Math.SQRT2;
      x *= inv;
      y *= inv;
    }
    return mitTempo({ x, y }, [...RENN_TASTEN].some((taste) => this.pressed.has(taste)));
  }

  private isTyping(): boolean {
    const el = document.activeElement;
    if (!el) return false;
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || (el as HTMLElement).isContentEditable;
  }
}
